/**
 * ai-image-generator.js
 * =====================
 * Модуль генерации AI-изображений кухни через OpenAI gpt-image-1.
 *
 * ИСПОЛЬЗОВАНИЕ в script.js:
 *   import { initAIImageGenerator } from './js/ai-image-generator.js';
 *   // После загрузки модели:
 *   initAIImageGenerator({ renderer, camera, scene, getConfig });
 *
 * getConfig — функция, возвращающая объект текущей конфигурации вида:
 *   {
 *     frontColor:   { value: '#acad93', text: 'Muted Olive' },
 *     bodyColor:    { value: '#24324A', text: 'Atlantic Night' },
 *     tableColor:   { value: '#7e937b', text: 'Moss Green' },
 *     countertop:   { value: 'wood',   text: 'Wood' },
 *     hasTable:     true,
 *   }
 */

// ──────────────────────────────────────────────────────────
// Строим финальный промт на основе конфигурации + текста юзера
// ──────────────────────────────────────────────────────────
function buildPrompt(userText) {
    const base = `Transform this 3D configurator screenshot into an ultra-photorealistic professional render or photograph.

CRITICAL — MUST PRESERVE EXACTLY:
• Every object, element and item visible in the reference image — do not add, remove or replace anything
• The exact camera angle, perspective and composition
• All colors, materials and finishes as shown — reproduce them with photorealistic accuracy
• The precise spatial layout, proportions, scale and positioning of all elements
• Every design detail, shape and silhouette

RENDER QUALITY REQUIREMENTS:
• Ultra-photorealistic quality indistinguishable from a real photograph or high-end CGI render
• Physically accurate lighting with soft shadows, ambient occlusion and realistic light bounces
• High-fidelity surface materials: accurate reflections, subsurface scattering, micro-texture detail
• Sharp focus on the subject with natural depth of field
• Professional color grading — neutral, balanced, true-to-life tones
• No artifacts, noise or AI distortions

LIGHTING:
• Natural soft daylight as primary source
• Subtle fill light to avoid harsh shadows
• Realistic environment reflections on glossy surfaces

OUTPUT STYLE:
• Professional product / architectural / commercial photography aesthetic
• Clean background that complements the subject
• Studio or real-world environment appropriate to the object type
• Resolution and detail level: 8K photorealistic CGI`;

    return userText?.trim()
        ? `${base} Additional details: ${userText.trim()}.`
        : base;
}

// ──────────────────────────────────────────────────────────
// Скриншот текущего Three.js канваса → base64 PNG (квадрат)
// gpt-image-1 /edits требует квадратное изображение
// ──────────────────────────────────────────────────────────
function captureCanvas(renderer, camera, scene) {
    // Рендерим свежий кадр
    renderer.render(scene, camera);
    const srcCanvas = renderer.domElement;
    const srcW = srcCanvas.width;
    const srcH = srcCanvas.height;

    // Масштабируем до 1024px по длинной стороне, сохраняя aspect ratio
    const TARGET = 1024;
    let outW, outH;
    if (srcW >= srcH) {
        outW = TARGET;
        outH = Math.round((srcH / srcW) * TARGET);
    } else {
        outH = TARGET;
        outW = Math.round((srcW / srcH) * TARGET);
    }

    const outCanvas = document.createElement('canvas');
    outCanvas.width  = outW;
    outCanvas.height = outH;
    const ctx = outCanvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(srcCanvas, 0, 0, outW, outH);

    return { dataUrl: outCanvas.toDataURL('image/png'), width: outW, height: outH };
}

// ──────────────────────────────────────────────────────────
// Вызов OpenAI Images API (gpt-image-1, редактирование)
// Отправляем скриншот как reference image + промт
// ──────────────────────────────────────────────────────────
async function generateImage(prompt, screenshot) {
    const { dataUrl, width, height } = screenshot;

    // Выбираем ближайший допустимый размер API: 1024x1024, 1536x1024, 1024x1536
    const VALID_SIZES = [
        { w: 1024, h: 1024 },
        { w: 1536, h: 1024 },
        { w: 1024, h: 1536 },
    ];
    const ratio = width / height;
    const best = VALID_SIZES.reduce((prev, cur) => {
        return Math.abs(cur.w / cur.h - ratio) < Math.abs(prev.w / prev.h - ratio) ? cur : prev;
    });
    const apiSize = `${best.w}x${best.h}`;

    // Масштабируем canvas до финального размера API
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width  = best.w;
    finalCanvas.height = best.h;
    const fCtx = finalCanvas.getContext('2d');
    fCtx.imageSmoothingEnabled = true;
    fCtx.imageSmoothingQuality = 'high';
    const img = new Image();
    await new Promise(res => { img.onload = res; img.src = dataUrl; });
    fCtx.drawImage(img, 0, 0, best.w, best.h);
    const finalDataUrl = finalCanvas.toDataURL('image/png');

    // Извлекаем чистый base64 без префикса data:...
    const base64 = finalDataUrl.split(',')[1];

    // Отправляем на бэкенд
    const response = await fetch('https://avada-media.com/api/demo-apps-page/create-image', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            image_base_64: base64,
            image_extension: 'png',
            image_size: apiSize,
            description: prompt,
        }),
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.detail[0]?.msg ?? `API error ${response.status}`);
    }

    const data = await response.json();

    if (!data?.image_base_64) {
        throw new Error('No image in response');
    }

    return `data:image/png;base64,${data.image_base_64}`;
}

// ──────────────────────────────────────────────────────────
// Анимация подсказок во время загрузки
// ──────────────────────────────────────────────────────────
const LOADING_HINTS = [
    'Analyzing configuration…',
    'Composing the scene…',
    'Applying materials and lighting…',
    'Refining photorealistic details…',
    'Almost done…',
];

function startLoadingHints(hintEl) {
    let i = 0;
    hintEl.textContent = LOADING_HINTS[0];
    const id = setInterval(() => {
        i = (i + 1) % LOADING_HINTS.length;
        hintEl.textContent = LOADING_HINTS[i];
    }, 2400);
    return () => clearInterval(id);
}

// ──────────────────────────────────────────────────────────
// Показать/скрыть шаги
// ──────────────────────────────────────────────────────────
function showStep(stepNum) {
    [1, 2, 3, 4].forEach(n => {
        const el = document.getElementById(`aiStep${n}`);
        if (el) el.classList.toggle('ai-modal__step--hidden', n !== stepNum);
    });
}

// ──────────────────────────────────────────────────────────
// Скачать изображение
// ──────────────────────────────────────────────────────────
function downloadImage(dataUrl) {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `kitchen-ai-${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

// ──────────────────────────────────────────────────────────
// ГЛАВНАЯ ФУНКЦИЯ ИНИЦИАЛИЗАЦИИ
// ──────────────────────────────────────────────────────────
export function initAIImageGenerator({
                                         renderer,
                                         camera,
                                         scene,
                                     }) {
    // DOM-элементы
    const overlay       = document.getElementById('aiModalOverlay');
    const closeBtn      = document.getElementById('aiModalClose');
    const triggerBtn    = document.getElementById('aiGenBtn');
    const generateBtn   = document.getElementById('aiGenerateBtn');
    const userPromptEl  = document.getElementById('aiUserPrompt');
    const hintEl        = document.getElementById('aiLoadingHint');
    const resultImg     = document.getElementById('aiResultImg');
    const downloadBtn   = document.getElementById('aiDownloadBtn');
    const retryBtn      = document.getElementById('aiRetryBtn');
    const errorRetryBtn = document.getElementById('aiErrorRetryBtn');
    const errorTextEl   = document.getElementById('aiErrorText');

    if (!overlay || !triggerBtn) {
        console.warn('[AIImageGenerator] Required DOM elements not found.');
        return;
    }

    let currentImageUrl = null;
    let stopHints = null;

    // Открыть модалку
    function openModal() {
        showStep(1);
        userPromptEl.value = '';
        overlay.classList.add('is-open');
        setTimeout(() => userPromptEl.focus(), 300);
    }

    // Закрыть модалку
    function closeModal() {
        overlay.classList.remove('is-open');
        if (stopHints) { stopHints(); stopHints = null; }
    }

    // Запустить генерацию
    async function runGeneration() {
        const userText = userPromptEl?.value ?? '';
        const prompt = buildPrompt(userText);

        // Скриншот канваса
        const screenshot = captureCanvas(renderer, camera, scene);

        // Показываем лоадер
        showStep(2);
        stopHints = startLoadingHints(hintEl);
        generateBtn.disabled = true;

        try {
            const imgUrl = await generateImage(prompt, screenshot);
            currentImageUrl = imgUrl;

            // Показываем результат
            resultImg.src = imgUrl;
            showStep(3);
        } catch (err) {
            console.error('[AIImageGenerator] Error:', err);
            errorTextEl.textContent = err.message ?? 'Failed to generate the image. Please try again.';
            showStep(4);
        } finally {
            if (stopHints) { stopHints(); stopHints = null; }
            generateBtn.disabled = false;
        }
    }

    // ── Слушатели событий ──

    triggerBtn.addEventListener('click', openModal);

    closeBtn.addEventListener('click', closeModal);

    // Закрытие по клику на overlay (вне модалки)
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) closeModal();
    });

    // Закрытие по Escape
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && overlay.classList.contains('is-open')) closeModal();
    });

    generateBtn.addEventListener('click', runGeneration);

    // Повторная генерация (кнопка в результате)
    retryBtn?.addEventListener('click', () => {
        showStep(1);
    });

    // Повторная генерация (кнопка в ошибке)
    errorRetryBtn?.addEventListener('click', () => {
        showStep(1);
    });

    // Скачать
    downloadBtn?.addEventListener('click', () => {
        if (currentImageUrl) downloadImage(currentImageUrl);
    });
}